/**
 * Analytics page filter helpers.
 *
 * Keeps the tab, range, and live-tail state shareable via URL query params.
 *
 * @copyright 2025 Glimbr Pty Ltd
 * @version 2026-04-22-v1
 */

export type AnalyticsTab = 'overview' | 'funnels' | 'cohorts';
export type AnalyticsDateRange = '7d' | '30d' | '90d';

export const ANALYTICS_TABS: readonly AnalyticsTab[] = ['overview', 'funnels', 'cohorts'];
export const ANALYTICS_DATE_RANGES: readonly AnalyticsDateRange[] = ['7d', '30d', '90d'];
export const DEFAULT_ANALYTICS_TAB: AnalyticsTab = 'overview';
export const DEFAULT_ANALYTICS_DATE_RANGE: AnalyticsDateRange = '30d';
export const DEFAULT_LIVE_TAIL = true;

export function isAnalyticsTab(value: string | null): value is AnalyticsTab {
    return value === 'overview' || value === 'funnels' || value === 'cohorts';
}

export function parseAnalyticsTab(value: string | null): AnalyticsTab {
    return isAnalyticsTab(value) ? value : DEFAULT_ANALYTICS_TAB;
}

export function isAnalyticsDateRange(value: string | null): value is AnalyticsDateRange {
    return value === '7d' || value === '30d' || value === '90d';
}

export function parseAnalyticsDateRange(value: string | null): AnalyticsDateRange {
    return isAnalyticsDateRange(value) ? value : DEFAULT_ANALYTICS_DATE_RANGE;
}

export function parseLiveTail(value: string | null): boolean {
    if (value === null) return DEFAULT_LIVE_TAIL;
    return value !== '0' && value !== 'false' && value !== 'off';
}

export function normalizeEventTypePrefix(value: string | null) {
    return (value ?? '')
        .trim()
        .toLowerCase()
        .replace(/^[.\s]+|[.\s]+$/g, '');
}

export function analyticsDateRangeDays(range: AnalyticsDateRange) {
    return Number(range.replace('d', ''));
}
